import { useMemo } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { LegendHint } from "../LegendHint";
import { GroupedByCostSource, GroupedByCostSourceSeries } from "../types";
import {
  defaultLayout,
  getColorBySource,
  setSelectedDateFromPlotClick,
} from "../plotting";
import { Layout, PlotData } from "plotly.js";
import { CostTableSource, MassDenominator } from "src/store/api/generatedApi";
import { useUnitsFormatter } from "hooks/formatters";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

interface Props {
  groupedBySourceCost: GroupedByCostSource;
  selectedTableSource: CostTableSource;
  selectedMassDenominator: MassDenominator;
  selectedSources: string[];
  setSelectedDate: (date: Date) => void;
}

const buildFigDataFromSeries = (
  series: GroupedByCostSourceSeries,
  availableSources: string[],
  t: TFunction
): Partial<PlotData> => {
  return {
    x: series.items.map((p) => p.date),
    y: series.items.map((p) => p.cost),
    type: "scatter",
    mode: "lines+markers",
    name: t(series.source),
    line: {
      color: getColorBySource(availableSources, series.source),
    },
    hovertemplate:
      "%{x|%b %Y}<br>%{y:.2f}<extra>" + t(series.source) + "</extra>",
  };
};

const buildInitialFigData = (
  { seriesCollection, availableSources }: GroupedByCostSource,
  selectedMassDenominator: MassDenominator,
  selectedSources: string[],
  t: TFunction
): Partial<PlotData>[] => {
  return seriesCollection
    .filter(
      (series) =>
        series.massDenominator === selectedMassDenominator &&
        selectedSources.includes(series.source)
    )
    .map((series) => buildFigDataFromSeries(series, availableSources, t));
};

export const CostPlot = ({
  groupedBySourceCost,
  selectedTableSource,
  selectedMassDenominator,
  selectedSources,
  setSelectedDate,
}: Props) => {
  const { t } = useTranslation();
  const units = useUnitsFormatter(true);
  const rangemode = groupedBySourceCost.rangeMode;

  const layout: Partial<Layout> = useMemo(() => {
    return {
      ...defaultLayout,
      xaxis: {
        type: "date",
        dtick: "M1",
      },
      yaxis: {
        ...defaultLayout.yaxis,
        title: { text: `${t(selectedTableSource)} ${units("specific_cost")}` },
        rangemode,
      },
      legend: {
        ...defaultLayout.legend,
        orientation: "h",
        y: -0.2,
        xanchor: "center",
      },
    };
  }, [rangemode, t, selectedTableSource, units]);

  const data = useMemo(
    () =>
      buildInitialFigData(
        groupedBySourceCost,
        selectedMassDenominator,
        selectedSources,
        t
      ),
    [groupedBySourceCost, selectedMassDenominator, selectedSources, t]
  );

  const onClick = useMemo(
    () => setSelectedDateFromPlotClick(setSelectedDate),
    [setSelectedDate]
  );

  return (
    <div style={{ width: "100%" }}>
      <Plot
        data={data}
        layout={layout}
        style={{ width: "100%" }}
        useResizeHandler
        onClick={onClick}
      />
      <LegendHint />
    </div>
  );
};
